// New-comparability scenario solver (one-shot plan §"Scenario solver").
//
// The proven flat-staff binary search stays the engine for the common design;
// this module puts it behind a generalized, goal-driven interface:
//
//   maximize-targets          — fill each target's §415(c) headroom, solve the
//                               smallest passing staff rate.
//   fixed-target-dollars      — targets get requested dollars (never above
//                               headroom; reductions are reported, not hidden).
//   fixed-target-percentages  — targets get a percent of allocation comp.
//   budget-cap                — largest uniform target fill whose total
//                               employer cost fits the budget.
//   repair-current-design     — hold the current design and either raise NHCE
//                               rates or scale target dollars down to passing,
//                               per the caller's preference. Never changes the
//                               document's gateway route or group definitions.
//
// Every candidate is validated through the SAME complete compliance pipeline
// (cross-testing incl. dual-path gateway, coverage, §415(c) schedule, §404
// screen, top-heavy minimum) — no abbreviated tests inside the search. All
// money is integer cents; searched percentages are whole basis points; results
// are deterministic: identical normalized inputs produce identical scenarios.
//
// MODELED, not a formal test or advice — statuses carry the shared honest
// contract from compliance/complianceStatus.js.

import { evaluateCrossTesting } from './compliance/crossTesting.js'
import { evaluateCoverage } from './compliance/coverage.js'
import { buildSection415Schedule } from './compliance/annualAdditions.js'
import { evaluateDeduction404Screen } from './compliance/deductionScreen.js'
import {
  combineComplianceStatuses,
  computeInputFingerprint,
  makeAssumption,
  makeComplianceStatus,
  makeLimitation,
  makeReason,
  NEW_COMP_CALCULATION_VERSION,
} from './compliance/complianceStatus.js'
import {
  evaluateNewCompReadiness,
  readinessBlockerReasons,
  readinessDisclosures,
  toBasisPoints,
  toCents,
} from './newCompInputs.js'
import { NEW_COMP_MODELING_DEFAULTS, SCENARIO_SEARCH_LIMITS } from './modelingDefaults.js'
import { RETIRA_AGE_WEIGHTED_ASSUMPTION_SET } from './compliance/ageWeightedAssumptions.js'

export const NEW_COMP_OBJECTIVES = Object.freeze([
  'maximize-targets',
  'fixed-target-dollars',
  'fixed-target-percentages',
  'budget-cap',
  'repair-current-design',
])

const ROLE_TARGET = 'target'
const ROLE_NON_TARGET_HCE = 'non-target-hce'
const ROLE_STAFF = 'staff'

const MAX_EVALUATIONS = SCENARIO_SEARCH_LIMITS.maxEvaluations

// ---------------------------------------------------------------------------
// Participant preparation
// ---------------------------------------------------------------------------

function preparePeople(participants, goal, groupControls) {
  const targetIdSet = new Set(goal.targetIds || [])
  const controls = (Array.isArray(groupControls) ? groupControls : [])
    .map((control) => ({
      id: String(control.id || control.label || 'group'),
      label: String(control.label || control.id || 'Group'),
      objectiveCriterion: control.objectiveCriterion ?? null,
      memberIds: new Set(Array.isArray(control.memberIds) ? control.memberIds : []),
      formula: ['percent', 'flat-dollar', 'fill-415c'].includes(control.formula) ? control.formula : 'percent',
      fixed: Number.isFinite(Number(control.fixed)) ? Number(control.fixed) : null,
      minimumBp: toBasisPoints(control.minimumPercent || 0),
      maximumBp: control.maximumPercent === null || control.maximumPercent === undefined
        ? null : toBasisPoints(control.maximumPercent),
      minimumDollarsCents: toCents(control.minimumDollars || 0),
      maximumDollarsCents: control.maximumDollars === null || control.maximumDollars === undefined
        ? null : toCents(control.maximumDollars),
      priority: Number.isFinite(Number(control.priority)) ? Number(control.priority) : 0,
    }))
    .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id))

  const people = (Array.isArray(participants) ? participants : []).filter(Boolean).map((person) => {
    const role = targetIdSet.has(person.id)
      ? ROLE_TARGET
      : (person.isHCE ? ROLE_NON_TARGET_HCE : ROLE_STAFF)
    const control = role === ROLE_STAFF
      ? controls.find((entry) => entry.memberIds.has(person.id)) || null
      : null
    return {
      ...person,
      role,
      control,
      compCents: toCents(person.allocationCompensation),
      excludable: !person.nonexcludableForCoverage,
      includedInAllocation: person.profitSharingEligible !== false
        && (Number(person.allocationCompensation) || 0) > 0,
    }
  })
  return { people, controls }
}

// ---------------------------------------------------------------------------
// Candidate allocation (pure, integer cents)
// ---------------------------------------------------------------------------

function targetRequestedCents(person, goal) {
  if (goal.objective === 'fixed-target-dollars') {
    return Math.max(0, toCents(goal.targetsById?.[person.id]))
  }
  if (goal.objective === 'fixed-target-percentages') {
    const bp = toBasisPoints(goal.targetsById?.[person.id] || 0)
    return Math.round(person.compCents * bp / 10000)
  }
  // maximize-targets, budget-cap (scaled elsewhere), repair (held elsewhere)
  return person.remaining415cHeadroomCents
}

// The candidate's requested PS cents for every person. `staffBp` drives the
// adjustable groups; frozen/fixed group formulas and target amounts are inputs.
function requestedCentsFor(person, staffBp, goal, options, overridesById) {
  if (overridesById && overridesById[person.id] !== undefined) return overridesById[person.id]
  if (!person.includedInAllocation || person.excludable) return 0
  if (person.role === ROLE_TARGET) {
    const scale = options.targetScaleBp ?? 10000
    return Math.round(targetRequestedCents(person, goal) * scale / 10000)
  }
  if (person.role === ROLE_NON_TARGET_HCE) {
    const bp = toBasisPoints(options.nonTargetHcePercent || 0)
    return Math.round(person.compCents * bp / 10000)
  }
  const control = person.control
  if (control) {
    if (control.formula === 'flat-dollar' && control.fixed !== null) {
      return Math.max(control.minimumDollarsCents, Math.min(
        control.maximumDollarsCents ?? Number.MAX_SAFE_INTEGER, toCents(control.fixed)))
    }
    if (control.formula === 'fill-415c') return person.remaining415cHeadroomCents
    if (control.formula === 'percent' && control.fixed !== null) {
      return Math.round(person.compCents * toBasisPoints(control.fixed) / 10000)
    }
    const groupBp = options.groupBpById?.[control.id]
      ?? Math.max(control.minimumBp, Math.min(control.maximumBp ?? Number.MAX_SAFE_INTEGER, staffBp))
    return Math.round(person.compCents * groupBp / 10000)
  }
  return Math.round(person.compCents * staffBp / 10000)
}

// ---------------------------------------------------------------------------
// The one complete compliance pipeline every candidate goes through
// ---------------------------------------------------------------------------

function runPipeline(context, staffBp, { overridesById = null, targetScaleBp = null, groupBpById = null } = {}) {
  const { people, goal, options, planFacts } = context
  context.evaluations += 1
  const pipelineOptions = {
    ...options,
    targetScaleBp: targetScaleBp ?? options.targetScaleBp ?? 10000,
    groupBpById,
  }
  const requestedById = {}
  for (const person of people) {
    requestedById[person.id] = requestedCentsFor(person, staffBp, goal, pipelineOptions, overridesById)
  }
  const schedule = buildSection415Schedule(people, { proposedProfitSharingCentsById: requestedById })
  const finalById = Object.fromEntries(schedule.rows.map((row) => [row.id, row.profitSharingFinalCents]))

  const crossRows = people.map((person) => {
    const psCents = finalById[person.id] ?? 0
    const nonelective = (person.otherNonelectiveCents + person.safeHarborNonelectiveCents
      + person.qnecCents + person.forfeitureCents + psCents) / 100
    return {
      id: person.id,
      isHCE: person.isHCE,
      excludable: person.excludable
        || (planFacts.otherwiseExcludableElection === true && person.otherwiseExcludable === true),
      allocation: nonelective,
      abptAllocation: nonelective + (person.deferralCents + person.matchCents) / 100,
      comp: person.allocationCompensation,
      gatewayComp: person.section415Compensation,
      age: person.age,
      testingAge: person.testingAge,
    }
  })
  const crossTesting = evaluateCrossTesting(crossRows, {
    planYear: planFacts.planYear,
    gatewayMinimumMethod: planFacts.gatewayMinimumMethod,
    gatewayExempt: planFacts.gatewayRoute !== 'minimum-allocation' || undefined,
    gatewayRoute: planFacts.gatewayRoute,
    otherwiseExcludableApplied: planFacts.otherwiseExcludableElection === true,
    imputePermittedDisparity: planFacts.imputedDisparityElection === true,
    rateBanding: planFacts.rateBandingElection === true,
    assumptionSet: planFacts.testingAssumptionSet || RETIRA_AGE_WEIGHTED_ASSUMPTION_SET,
  })
  const gatewayRouteQualification = evaluateGatewayRouteQualification(crossTesting, planFacts)

  const nonexcludable = people.filter((person) => !person.excludable)
  const benefits = (person) => (finalById[person.id] ?? 0)
    + person.otherNonelectiveCents + person.safeHarborNonelectiveCents
    + person.qnecCents + person.forfeitureCents
  const coverage = evaluateCoverage({
    hceBenefiting: nonexcludable.filter((p) => p.isHCE && benefits(p) > 0).length,
    nonexcludableHceCount: nonexcludable.filter((p) => p.isHCE).length,
    nhceBenefiting: nonexcludable.filter((p) => !p.isHCE && benefits(p) > 0).length,
    nonexcludableNhceCount: nonexcludable.filter((p) => !p.isHCE).length,
    hceAvgBenefitPercent: crossTesting.available ? crossTesting.hceAvgEbar : null,
    nhceAvgBenefitPercent: crossTesting.available ? crossTesting.nhceAvgEbar : null,
  })

  const deduction = evaluateDeduction404Screen(people, {
    proposedProfitSharingCentsById: finalById,
  })

  // Top-heavy minimum: modeled only with a supplied or assumed status.
  const topHeavy = evaluateTopHeavyConstraint(people, finalById, planFacts)

  const employerCostCents = people.reduce((sum, person) => sum
    + (finalById[person.id] ?? 0) + person.matchCents + person.otherNonelectiveCents
    + person.safeHarborNonelectiveCents + person.qnecCents, 0)
  const profitSharingCents = Object.values(finalById).reduce((sum, value) => sum + value, 0)

  return {
    staffBp,
    requestedById,
    finalById,
    schedule,
    crossTesting,
    gatewayRouteQualification,
    coverage,
    deduction,
    topHeavy,
    employerCostCents,
    profitSharingCents,
  }
}

function evaluateGatewayRouteQualification(crossTesting, planFacts) {
  const route = planFacts.gatewayRoute || 'minimum-allocation'
  if (route === 'minimum-allocation') return { route, status: 'passes', groups: [] }
  const confirmation = planFacts.gatewayRouteQualification
  const confirmed = confirmation?.confirmed === true
    && ['plan-document', 'tpa', 'actuary'].includes(confirmation?.source)

  if (route === 'broadly-available') {
    const rows = crossTesting.available ? crossTesting.rows || [] : []
    const hces = rows.filter((row) => row.isHCE)
    const nhces = rows.filter((row) => !row.isHCE)
    const rates = [...new Set(rows.map((row) => Math.round(row.gatewayRate * 100) / 100))]
    const groups = rates.map((rate) => {
      const atRate = (row) => Math.abs(row.gatewayRate - rate) <= 0.005
      const hcePercent = hces.length ? hces.filter(atRate).length / hces.length * 100 : 0
      const nhcePercent = nhces.length ? nhces.filter(atRate).length / nhces.length * 100 : 0
      const ratioPercent = hcePercent > 0 ? nhcePercent / hcePercent * 100 : 100
      return { ratePercent: rate, hcePercent, nhcePercent, ratioPercent, passes: ratioPercent >= 70 }
    })
    if (groups.some((group) => !group.passes)) {
      return { route, status: 'fails', groups, confirmed }
    }
    return { route, status: confirmed ? 'passes' : 'requires-review', groups, confirmed }
  }

  // A gradual age/service route depends on the document's single banded
  // schedule, equal intervals, and smooth-increase rules. Census allocations
  // alone do not establish those document terms.
  return {
    route,
    status: confirmed ? 'passes' : 'requires-review',
    groups: [],
    confirmed,
  }
}

function evaluateTopHeavyConstraint(people, finalById, planFacts) {
  const facts = planFacts.topHeavy || { status: 'unknown' }
  if (facts.status === 'not-top-heavy') {
    return { ...makeComplianceStatus({ status: 'not-applicable' }), modeledMinimumPercent: 0, shortfallCents: 0, rows: [] }
  }
  if (facts.status !== 'top-heavy') {
    return {
      ...makeComplianceStatus({
        status: 'requires-review',
        limitations: [makeLimitation('top-heavy-unknown',
          'Top-heavy status is unknown; the §416 minimum-allocation consequence is not modeled.')],
      }),
      modeledMinimumPercent: null,
      shortfallCents: null,
      rows: [],
    }
  }
  const minimumPercent = Number.isFinite(Number(facts.minimumPercent)) ? Number(facts.minimumPercent) : 3
  const rows = []
  let shortfallCents = 0
  for (const person of people) {
    if (person.isKey || person.excludable || person.eligibilityStatus !== 'eligible') continue
    const requiredCents = Math.round(person.compCents * toBasisPoints(minimumPercent) / 10000)
    const providedCents = (finalById[person.id] ?? 0) + person.matchCents
      + person.otherNonelectiveCents + person.safeHarborNonelectiveCents + person.qnecCents
      + person.forfeitureCents
    const shortfall = Math.max(0, requiredCents - providedCents)
    shortfallCents += shortfall
    if (shortfall > 0) rows.push({ id: person.id, requiredCents, providedCents, shortfallCents: shortfall })
  }
  const assumed = facts.source === 'assumed'
  return {
    ...makeComplianceStatus({
      status: shortfallCents > 0 ? 'fails' : (assumed ? 'requires-review' : 'passes'),
      reasons: shortfallCents > 0
        ? [makeReason('top-heavy-minimum-shortfall',
            `Non-key participants are short $${(shortfallCents / 100).toFixed(2)} of the ${minimumPercent}% top-heavy minimum.`,
            { employeeIds: rows.map((row) => row.id) })]
        : [],
      assumptions: assumed
        ? [makeAssumption('top-heavy-assumed', 'Top-heavy status assumed', 'top-heavy', { source: 'user', estimated: true })]
        : [],
    }),
    modeledMinimumPercent: minimumPercent,
    shortfallCents,
    rows,
  }
}

// Core constraints that are monotone as a uniform staff rate rises. A broadly
// available allocation-rate route is deliberately excluded: exact rate-group
// availability can pass at an intermediate rate and fail again above it.
const monotoneCoreTestsPass = (result) => result.crossTesting.available
  && result.crossTesting.gatewaySatisfied
  && result.crossTesting.generalTestPasses
  && result.coverage.available
  && result.coverage.status !== 'fails'
  && result.schedule.status !== 'fails'
  && (result.topHeavy.shortfallCents === null || result.topHeavy.shortfallCents === 0)

const monotoneTestsPass = (result) => monotoneCoreTestsPass(result)
  && result.gatewayRouteQualification.status !== 'fails'

// ---------------------------------------------------------------------------
// Staff-rate search (exact to the basis point; proven monotone)
// ---------------------------------------------------------------------------

function staffFloorBp(context) {
  const { people, options, planFacts } = context
  let floor = toBasisPoints(options.minStaffPercent || 0)
  const minTotalPct = planFacts.topHeavy?.status === 'top-heavy'
    ? (Number(planFacts.topHeavy.minimumPercent) || 3)
    : 0
  for (const person of people) {
    if (person.role !== ROLE_STAFF || person.excludable || !person.includedInAllocation) continue
    if (person.control) {
      floor = Math.max(floor, person.control.minimumBp)
      continue
    }
    if (minTotalPct > 0 && person.compCents > 0) {
      const existingCents = person.otherNonelectiveCents + person.safeHarborNonelectiveCents
        + person.qnecCents + person.forfeitureCents + person.matchCents
      const neededCents = Math.round(person.compCents * toBasisPoints(minTotalPct) / 10000) - existingCents
      if (neededCents > 0) {
        // Smallest whole bp whose ROUNDED cent allocation covers the need —
        // the allocation itself rounds, so the comparison must too.
        floor = Math.max(floor, Math.ceil(((neededCents - 0.5) * 10000) / person.compCents))
      }
    }
  }
  return floor
}

function solveMinimalStaffBp(context, pipelineExtras = {}) {
  const floorBp = staffFloorBp(context)
  const maxBp = toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)
  const atFloor = runPipeline(context, floorBp, pipelineExtras)
  if (!atFloor.crossTesting.available) return { status: 'unavailable', reason: atFloor.crossTesting.reason, atFloor, floorBp, maxBp }
  if (monotoneTestsPass(atFloor)) return { status: 'solved', staffBp: floorBp, result: atFloor, floorBp, maxBp }
  if (floorBp >= maxBp) return { status: 'infeasible', atMax: atFloor, floorBp, maxBp }
  const atMax = runPipeline(context, maxBp, pipelineExtras)
  if (!monotoneCoreTestsPass(atMax)) return { status: 'infeasible', atMax, floorBp, maxBp }
  let lo = floorBp
  let hi = maxBp
  while (hi - lo > 1 && context.evaluations < MAX_EVALUATIONS) {
    const mid = Math.floor((lo + hi) / 2)
    if (monotoneCoreTestsPass(runPipeline(context, mid, pipelineExtras))) hi = mid
    else lo = mid
  }
  const coreMinimum = runPipeline(context, hi, pipelineExtras)
  if (monotoneTestsPass(coreMinimum)) {
    return { status: 'solved', staffBp: hi, result: coreMinimum, floorBp, maxBp }
  }
  // Non-minimum gateway routes are document/availability formulas, not a
  // monotone numeric constraint. Scan upward at the engine's exact 1-bp
  // precision so an intermediate qualifying rate cannot be skipped merely
  // because the maximum rate fails a different availability group.
  if (context.planFacts.gatewayRoute !== 'minimum-allocation') {
    for (let bp = hi + 1; bp <= maxBp; bp += 1) {
      const candidate = bp === maxBp ? atMax : runPipeline(context, bp, pipelineExtras)
      if (monotoneTestsPass(candidate)) {
        return { status: 'solved', staffBp: bp, result: candidate, floorBp, maxBp }
      }
    }
  }
  return { status: 'infeasible', atMax, floorBp, maxBp }
}

// Per-group refinement for multi-group designs: lower one adjustable group at
// a time (deterministic id order) to its own minimal passing rate while the
// others hold. Each per-group search is monotone, so it is exact per group,
// but the combined result is a heuristic — labeled lowest-cost-found.
function refineGroupRates(context, staffBp) {
  const adjustable = context.controls.filter((control) => control.formula === 'percent' && control.fixed === null)
  if (adjustable.length < 2) return null
  const groupBpById = {}
  for (const control of adjustable) {
    groupBpById[control.id] = Math.max(control.minimumBp, Math.min(control.maximumBp ?? staffBp, staffBp))
  }
  for (const control of adjustable) {
    let lo = control.minimumBp
    let hi = groupBpById[control.id]
    const passesAt = (bp) => monotoneTestsPass(runPipeline(context, staffBp, { groupBpById: { ...groupBpById, [control.id]: bp } }))
    if (passesAt(lo)) {
      groupBpById[control.id] = lo
      continue
    }
    while (hi - lo > 1 && context.evaluations < MAX_EVALUATIONS) {
      const mid = Math.floor((lo + hi) / 2)
      if (passesAt(mid)) hi = mid
      else lo = mid
    }
    groupBpById[control.id] = hi
  }
  return groupBpById
}

// ---------------------------------------------------------------------------
// Scenario assembly
// ---------------------------------------------------------------------------

function testStatuses(result, context) {
  const ct = result.crossTesting
  const routeQualification = result.gatewayRouteQualification
  const gatewayStatus = !ct.available
    ? 'unavailable'
    : !ct.gatewaySatisfied || routeQualification.status === 'fails'
      ? 'fails'
      : routeQualification.status
  const rateGroupStatus = !ct.available ? 'unavailable' : (ct.allRateGroupsPass ? 'passes' : 'fails')
  const abptStatus = !ct.available ? 'unavailable' : (ct.abptPass ? 'passes' : 'fails')
  const coverageStatus = !result.coverage.available
    ? 'unavailable'
    : result.coverage.status === 'fails' ? 'fails'
      : result.coverage.status === 'may-pass-average-benefit' ? 'requires-review' : 'passes'
  const bandingLimitations = context.planFacts.rateBandingElection
    ? [makeLimitation('rate-banding-anti-abuse',
        'Rate banding is modeled; the anti-abuse "significantly higher" grouping bar is a facts-and-circumstances determination a professional must make.')]
    : []
  return {
    gateway: makeComplianceStatus({
      status: gatewayStatus,
      reasons: gatewayStatus === 'fails'
        ? [routeQualification.status === 'fails'
            ? makeReason('gateway-route-not-qualified',
                'At least one allocation rate is not currently available to a §410(b) ratio-percentage group, so the model flags the broadly-available route for review.')
            : makeReason('gateway-shortfall',
                `${ct.gatewayShortfallCount} NHCE${ct.gatewayShortfallCount === 1 ? ' is' : 's are'} below the minimum gateway (about $${(ct.gatewayShortfallTotal || 0).toFixed(2)} to close).`)]
        : [],
      limitations: gatewayStatus === 'requires-review'
        ? [makeLimitation('gateway-route-document-review',
            `The ${routeQualification.route} gateway route requires its plan-document formula and availability conditions to be confirmed by the TPA before reliance.`)]
        : [],
    }),
    coverage: makeComplianceStatus({ status: coverageStatus }),
    rateGroups: makeComplianceStatus({
      status: rateGroupStatus,
      reasons: rateGroupStatus === 'fails'
        ? [makeReason('rate-group-failure', `${ct.failingRateGroupCount} HCE rate group${ct.failingRateGroupCount === 1 ? '' : 's'} fail${ct.failingRateGroupCount === 1 ? 's' : ''} §410(b).`)]
        : [],
      limitations: bandingLimitations,
    }),
    abpt: makeComplianceStatus({
      status: abptStatus,
      reasons: abptStatus === 'fails'
        ? [makeReason('abpt-failure', `The average benefit percentage is ${ct.abptRatio?.toFixed(2)}%, below 70%.`)]
        : [],
    }),
    section415c: makeComplianceStatus({
      status: result.schedule.status,
      reasons: result.schedule.reasons,
    }),
    deduction: makeComplianceStatus({
      status: result.deduction.status,
      reasons: result.deduction.reasons,
      limitations: result.deduction.limitations,
    }),
    topHeavy: makeComplianceStatus({
      status: result.topHeavy.status,
      reasons: result.topHeavy.reasons,
      assumptions: result.topHeavy.assumptions,
      limitations: result.topHeavy.limitations,
    }),
  }
}

function buildScenario(context, id, label, strategy, result, {
  optimality = 'exact-minimum',
  explanation = '',
  groupBpById = null,
} = {}) {
  const { people, goal, options } = context
  const tests = testStatuses(result, context)
  const disclosures = readinessDisclosures(context.readiness, context.planFacts)
  // An enabled cash-balance component makes the combined DB/DC path mandatory:
  // a DC-only evaluation can never present itself as a modeled pass.
  const combinedRequired = context.planFacts.cashBalance?.enabled === true
  const status = combineComplianceStatuses([
    ...Object.values(tests),
    makeComplianceStatus({
      status: (context.readiness.reviewItems.length || combinedRequired) ? 'requires-review' : 'passes',
      limitations: [
        ...disclosures.limitations,
        ...(combinedRequired
          ? [makeLimitation('combined-testing-required',
              'This plan has an enabled cash-balance component; this DC-only result is incomplete until the combined DB/DC tests (aggregate gateway, §401(a)(26), MVAR rate groups) are run in the combined designer.')]
          : []),
      ],
      assumptions: disclosures.assumptions,
    }),
  ], {
    rulesYear: context.planFacts.planYear,
    inputFingerprint: context.inputFingerprint,
  })

  const perPerson = people.map((person) => {
    const scheduleRow = result.schedule.rows.find((row) => row.id === person.id)
    const psCents = result.finalById[person.id] ?? 0
    const employerCents = psCents + person.matchCents + person.otherNonelectiveCents
      + person.safeHarborNonelectiveCents + person.qnecCents
    return {
      id: person.id,
      name: person.name,
      role: person.role,
      groupId: person.control?.id ?? null,
      isHCE: person.isHCE,
      compensationCents: person.compCents,
      deferralCents: person.deferralCents,
      matchCents: person.matchCents,
      safeHarborNonelectiveCents: person.safeHarborNonelectiveCents,
      otherNonelectiveCents: person.otherNonelectiveCents,
      nonelectiveCents: person.otherNonelectiveCents + person.safeHarborNonelectiveCents,
      qnecCents: person.qnecCents,
      forfeitureCents: person.forfeitureCents,
      profitSharingRequestedCents: result.requestedById[person.id] ?? 0,
      profitSharingReducedCents: scheduleRow?.profitSharingReducedCents ?? 0,
      profitSharingCents: psCents,
      profitSharingPercent: person.compCents > 0 ? (psCents / person.compCents) * 100 : 0,
      employerCents,
      remainingHeadroomCents: scheduleRow?.remainingHeadroomCents ?? 0,
      atLimit: scheduleRow?.atLimit === true,
    }
  })

  const targetRows = perPerson.filter((row) => row.role === ROLE_TARGET)
  const staffRows = perPerson.filter((row) => row.role === ROLE_STAFF)
  const targetPsCents = targetRows.reduce((sum, row) => sum + row.profitSharingCents, 0)
  const staffIncluded = staffRows.filter((row) => row.compensationCents > 0)
  const minimumStaffPercent = staffIncluded.length
    ? Math.min(...staffIncluded.map((row) => row.profitSharingPercent))
    : 0

  const component = buildSuggestedComponent(context, result)
  return {
    id,
    label,
    strategy,
    status,
    optimality,
    staffPercent: result.staffBp / 100,
    groupRates: groupBpById
      ? Object.fromEntries(Object.entries(groupBpById).map(([key, bp]) => [key, bp / 100]))
      : null,
    objectiveResult: {
      objective: goal.objective,
      targetIds: [...(goal.targetIds || [])],
      requestedCents: targetRows.reduce((sum, row) => sum + row.profitSharingRequestedCents, 0),
      achievedCents: targetPsCents,
      optimizeFor: goal.optimizeFor || 'lowest-employer-cost',
    },
    perPerson,
    totals: {
      employerCostCents: result.employerCostCents,
      profitSharingCents: result.profitSharingCents,
      targetProfitSharingCents: targetPsCents,
      nonTargetProfitSharingCents: result.profitSharingCents - targetPsCents,
      targetSharePercent: result.profitSharingCents > 0
        ? (targetPsCents / result.profitSharingCents) * 100 : 0,
      minimumStaffPercent,
    },
    tests,
    constraints: {
      staffFloorPercent: staffFloorBp(context) / 100,
      maxStaffPercent: context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent,
      budgetCents: goal.objective === 'budget-cap' ? toCents(goal.budget) : null,
      nonTargetHcePercent: options.nonTargetHcePercent || 0,
    },
    assumptions: status.assumptions,
    limitations: status.limitations,
    suggestedDesign: component.suggestedDesign,
    groupAssignments: component.groupAssignments,
    explanation,
    detail: {
      crossTesting: result.crossTesting,
      coverage: result.coverage,
      schedule415c: result.schedule,
      deduction: result.deduction,
      topHeavy: result.topHeavy,
    },
  }
}

function buildSuggestedComponent(context, result) {
  const { people, goal, options } = context
  const targetLabel = options.targetGroupLabel || 'Owner'
  const hceLabel = options.nonTargetHceGroupLabel || 'Non-Owner HCE'
  const staffLabel = options.staffGroupLabel || ''
  const targets = people.filter((person) => person.role === ROLE_TARGET)
  const fixedDollarGoal = goal.objective !== 'maximize-targets'
  const targetLabels = new Map(targets.map((person, index) => [
    person.id,
    targets.length === 1 || !fixedDollarGoal ? targetLabel : `${targetLabel} ${index + 1}`,
  ]))
  const groups = [
    fixedDollarGoal
      ? targets.map((person) => ({
          label: targetLabels.get(person.id),
          formula: { method: 'flat', amount: (result.finalById[person.id] ?? 0) / 100 },
        }))
      : { label: targetLabel, formula: { method: 'fill_415c' } },
    { label: hceLabel, formula: { method: 'percent', percent: Math.max(0, Number(options.nonTargetHcePercent) || 0) } },
    { label: staffLabel, formula: { method: 'percent', percent: result.staffBp / 100 } },
  ].flat().filter(Boolean)
  // Fixed-dollar goals with multiple targets need distinct groups; dedupe by
  // label defensively (labels are unique by construction above).
  const groupAssignments = Object.fromEntries(people.map((person) => [
    person.id,
    person.role === ROLE_TARGET
      ? targetLabels.get(person.id)
      : person.role === ROLE_NON_TARGET_HCE ? hceLabel : staffLabel,
  ]))
  return { suggestedDesign: { type: 'grouped', groups }, groupAssignments }
}

// ---------------------------------------------------------------------------
// Infeasibility diagnostics: name the binding constraint
// ---------------------------------------------------------------------------

function diagnoseInfeasible(context, atMax) {
  const ct = atMax.crossTesting
  const cappedGroups = context.controls
    .filter((control) => control.formula === 'percent' && control.fixed === null && control.maximumBp !== null)
  if (!ct.available) {
    return { bindingConstraint: 'inputs', message: `Testing is unavailable: ${ct.reason}.` }
  }
  if (atMax.gatewayRouteQualification?.status === 'fails') {
    return { bindingConstraint: 'gateway-route', message: 'The selected non-minimum gateway route is flagged because the current allocation-rate availability groups do not appear to support it.' }
  }
  if (atMax.coverage?.status === 'fails') {
    return { bindingConstraint: 'coverage', message: `The nonelective component's §410(b) ratio percentage is ${atMax.coverage.ratioPercent?.toFixed?.(2) ?? 'below the required range'}%. Add benefiting NHCEs or change the allocation groups.` }
  }
  if (atMax.schedule?.status === 'fails') {
    return { bindingConstraint: '415c-prior-additions', message: 'Existing annual additions exceed §415(c) for at least one participant before the proposed profit sharing.' }
  }
  if (!ct.gatewaySatisfied) {
    return cappedGroups.length
      ? { bindingConstraint: 'group-maximum', message: `A group maximum keeps ${ct.gatewayShortfallCount} NHCE(s) below the minimum gateway. Raise the cap on ${cappedGroups.map((c) => c.label).join(', ')} or lower the target allocations.` }
      : { bindingConstraint: 'gateway', message: `Even at the ${((context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)).toFixed(2)}% staff cap the model flags a minimum-gateway shortfall. Raise the staff cap or reduce target allocations.` }
  }
  if (!ct.abptPass) {
    return { bindingConstraint: 'abpt', message: `The average benefit percentage tops out at ${ct.abptRatio?.toFixed(2)}% (needs 70%). Reduce target allocations, add younger-staff contributions, or raise the staff cap.` }
  }
  if (!ct.allRateGroupsPass) {
    return { bindingConstraint: 'rate-group', message: `${ct.failingRateGroupCount} HCE rate group(s) are flagged below the modeled §410(b) threshold at the staff cap. Reduce the highest target allocation or add staff contributions.` }
  }
  if (atMax.topHeavy.shortfallCents > 0) {
    return { bindingConstraint: 'top-heavy-minimum', message: 'The model flags a potential top-heavy minimum shortfall within the staff-rate cap.' }
  }
  return { bindingConstraint: 'unknown', message: 'Every staff rate within the cap triggers at least one modeled review flag.' }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function solveNewCompScenarios({
  participants = [],
  planFacts = null,
  goal = {},
  options = {},
  readiness = null,
  reportInputs = {},
} = {}) {
  const resolvedGoal = {
    objective: NEW_COMP_OBJECTIVES.includes(goal.objective) ? goal.objective : 'maximize-targets',
    targetIds: Array.isArray(goal.targetIds) ? [...goal.targetIds] : [],
    targetsById: goal.targetsById || {},
    budget: goal.budget ?? null,
    optimizeFor: goal.optimizeFor || 'lowest-employer-cost',
    repairPreference: goal.repairPreference === 'decrease-hce' ? 'decrease-hce' : 'increase-nhce',
  }
  const facts = planFacts || {}
  const resolvedReadiness = readiness || evaluateNewCompReadiness({ planFacts: facts, participants })
  const inputFingerprint = computeInputFingerprint({
    goal: resolvedGoal,
    planFacts: facts,
    options: {
      nonTargetHcePercent: options.nonTargetHcePercent || 0,
      maxStaffPercent: options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent,
      minStaffPercent: options.minStaffPercent || 0,
    },
    participants: participants.map((person) => ({
      id: person.id,
      isHCE: person.isHCE,
      age: person.age,
      comp: person.allocationCompensation,
      comp415: person.section415Compensation,
      headroom: person.remaining415cHeadroomCents,
    })),
  })
  const base = {
    status: null,
    goal: resolvedGoal,
    readiness: resolvedReadiness,
    scenarios: [],
    diagnostics: null,
    calculationVersion: NEW_COMP_CALCULATION_VERSION,
    rulesYear: facts.planYear ?? null,
    inputFingerprint,
    planFacts: { ...facts },
    solverFacts: {
      options: { ...options },
      reportInputs: reportInputs && typeof reportInputs === 'object' ? { ...reportInputs } : {},
    },
  }

  if (resolvedReadiness.status === 'blocked') {
    return {
      ...base,
      status: 'blocked',
      blockedStatus: makeComplianceStatus({
        status: 'unavailable',
        reasons: readinessBlockerReasons(resolvedReadiness),
        rulesYear: facts.planYear ?? null,
        inputFingerprint,
      }),
    }
  }
  if (!resolvedGoal.targetIds.length && resolvedGoal.objective !== 'repair-current-design') {
    return {
      ...base,
      status: 'blocked',
      blockedStatus: makeComplianceStatus({
        status: 'unavailable',
        reasons: [makeReason('no-targets', 'Pick at least one owner or HCE to design around.')],
      }),
    }
  }

  const { people, controls } = preparePeople(participants, resolvedGoal, options.groupControls)
  const context = {
    people,
    controls,
    goal: resolvedGoal,
    options,
    planFacts: facts,
    readiness: resolvedReadiness,
    inputFingerprint,
    evaluations: 0,
  }

  const scenarios = []
  const pushScenario = (scenario) => {
    if (scenario && !scenarios.some((existing) => existing.id === scenario.id)) scenarios.push(scenario)
  }

  if (resolvedGoal.objective === 'repair-current-design') {
    return solveRepair(context, base, pushScenario, scenarios)
  }

  if (resolvedGoal.objective === 'budget-cap') {
    const budgetCents = toCents(resolvedGoal.budget)
    if (!(budgetCents > 0)) {
      return {
        ...base,
        status: 'blocked',
        blockedStatus: makeComplianceStatus({
          status: 'unavailable',
          reasons: [makeReason('no-budget', 'Enter the employer budget for a budget-capped design.')],
        }),
      }
    }
    return solveBudget(context, base, budgetCents, pushScenario, scenarios)
  }

  // maximize-targets / fixed-target-dollars / fixed-target-percentages
  const minimal = solveMinimalStaffBp(context)
  if (minimal.status === 'unavailable') {
    return { ...base, status: 'blocked', blockedStatus: makeComplianceStatus({ status: 'unavailable', reasons: [makeReason(minimal.reason || 'unavailable', `Testing is unavailable: ${minimal.reason}.`)] }) }
  }
  if (minimal.status === 'infeasible') {
    return { ...base, status: 'infeasible', diagnostics: diagnoseInfeasible(context, minimal.atMax) }
  }

  // Failed §404 screen is a solver CONSTRAINT: raising the staff rate only
  // makes it worse, so under a fill-the-targets goal the design is infeasible.
  // The failing minimal design is still returned for inspection.
  if (minimal.result.deduction.status === 'fails') {
    const failing = buildScenario(context, 'minimal-staff', 'Lowest staff cost', 'minimal-staff', minimal.result, {
      explanation: 'No nondiscrimination flag is modeled, but employer cost exceeds the §404(a)(3) deduction screen and is flagged for review.',
    })
    return {
      ...base,
      status: 'infeasible',
      scenarios: [failing],
      diagnostics: {
        bindingConstraint: 'deduction-screen',
        message: `Employer contributions exceed 25% of eligible-participant compensation by $${((-minimal.result.deduction.headroomCents) / 100).toFixed(2)}. Lower the target contributions or switch to a budget goal.`,
      },
    }
  }

  pushScenario(buildScenario(context, 'minimal-staff', 'Lowest staff cost', 'minimal-staff', minimal.result, {
    explanation: `The smallest staff rate (${(minimal.staffBp / 100).toFixed(2)}%) with no modeled screen flags — the least employer cost for this goal. This is not a formal determination.`,
  }))

  // Cushioned alternative: next whole quarter-percent above the exact minimum.
  const cushionBp = Math.ceil((minimal.staffBp + 1) / 25) * 25
  if (cushionBp > minimal.staffBp && cushionBp <= toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)) {
    const cushioned = runPipeline(context, cushionBp)
    if (monotoneTestsPass(cushioned)) {
      pushScenario(buildScenario(context, 'cushioned-staff', 'Cushioned staff rate', 'cushioned-staff', cushioned, {
        explanation: `Staff at ${(cushionBp / 100).toFixed(2)}% — a margin above the exact minimum so routine census drift is less likely to flip a test.`,
      }))
    }
  }

  // Deemed-5% alternative: staff at the flat 5% gateway safe harbor.
  const fiveBp = Math.max(500, staffFloorBp(context))
  if (fiveBp !== minimal.staffBp && fiveBp !== cushionBp && fiveBp <= toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)) {
    const five = runPipeline(context, fiveBp)
    if (monotoneTestsPass(five)) {
      pushScenario(buildScenario(context, 'five-percent-staff', '5% gateway cushion', 'five-percent-staff', five, {
        explanation: 'Staff at the flat 5% deemed-gateway level — the most defensible posture, at a higher employer cost.',
      }))
    }
  }

  // Multi-group refinement (lowest-cost-found, never labeled optimal).
  const refined = refineGroupRates(context, minimal.staffBp)
  if (refined) {
    const refinedResult = runPipeline(context, minimal.staffBp, { groupBpById: refined })
    if (monotoneTestsPass(refinedResult) && refinedResult.employerCostCents < minimal.result.employerCostCents) {
      pushScenario(buildScenario(context, 'per-group-refined', 'Per-group rates', 'per-group-refined', refinedResult, {
        optimality: 'lowest-cost-found',
        groupBpById: refined,
        explanation: 'Each adjustable group lowered to its own minimal passing rate (bounded search; lowest cost found, not proven optimal).',
      }))
    }
  }

  return finalizeScenarios(base, scenarios)
}

function finalizeScenarios(base, scenarios) {
  // Nondominated filter on (employer cost, target dollars): a scenario that
  // costs more without delivering more to the targets is dropped.
  const kept = scenarios.filter((scenario) => !scenarios.some((other) => other !== scenario
    && other.totals.employerCostCents <= scenario.totals.employerCostCents
    && other.totals.targetProfitSharingCents >= scenario.totals.targetProfitSharingCents
    && (other.totals.employerCostCents < scenario.totals.employerCostCents
      || other.totals.targetProfitSharingCents > scenario.totals.targetProfitSharingCents)))
  // Keep deliberately-defensive strategies even when dominated on cost alone.
  for (const scenario of scenarios) {
    if (!kept.includes(scenario) && ['cushioned-staff', 'five-percent-staff'].includes(scenario.strategy)) kept.push(scenario)
  }
  kept.sort((a, b) => a.totals.employerCostCents - b.totals.employerCostCents || a.id.localeCompare(b.id))
  return { ...base, status: 'solved', scenarios: kept.slice(0, 5) }
}

// Budget goal: the largest uniform target-fill scale whose minimal-staff design
// fits the budget. Monotone in the scale (bigger targets ⇒ higher staff rate ⇒
// higher cost), so a basis-point binary search is exact.
function solveBudget(context, base, budgetCents, pushScenario, scenarios) {
  // A candidate "fits" only when it fits the budget AND clears the §404
  // deduction screen — both shrink as the target fill scales down, so the
  // scale search stays monotone with the combined condition.
  const fits = (solved) => solved.result.employerCostCents <= budgetCents
    && solved.result.deduction.status !== 'fails'
  const costAtScale = (scaleBp) => {
    const solved = solveMinimalStaffBp(context, { targetScaleBp: scaleBp })
    return solved.status === 'solved' ? solved : null
  }
  const full = costAtScale(10000)
  if (!full) {
    const atMax = runPipeline(context, toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent), { targetScaleBp: 10000 })
    return { ...base, status: 'infeasible', diagnostics: diagnoseInfeasible(context, atMax) }
  }
  if (fits(full)) {
    pushScenario(buildScenario(context, 'budget-full-max', 'Full maximum fits the budget', 'budget-full-max', full.result, {
      explanation: 'Filling every target to the §415(c) limit stays inside the budget.',
    }))
    return finalizeScenarios(base, scenarios)
  }
  const zero = costAtScale(0)
  if (!zero || !fits(zero)) {
    return {
      ...base,
      status: 'infeasible',
      diagnostics: {
        bindingConstraint: 'budget',
        message: zero
          ? `Even with no target profit sharing, required staff and existing employer contributions cost $${(zero.result.employerCostCents / 100).toFixed(2)}, above the $${(budgetCents / 100).toFixed(2)} budget.`
          : 'No design within the staff-rate cap fits the budget.',
      },
    }
  }
  let lo = 0
  let hi = 10000
  while (hi - lo > 1 && context.evaluations < MAX_EVALUATIONS) {
    const mid = Math.floor((lo + hi) / 2)
    const solved = costAtScale(mid)
    if (solved && fits(solved)) lo = mid
    else hi = mid
  }
  const best = costAtScale(lo)
  if (best) {
    pushScenario(buildScenario(context, 'budget-best-fit', 'Best fit inside the budget', 'budget-best-fit', best.result, {
      explanation: `Targets filled to ${(lo / 100).toFixed(2)}% of their §415(c) headroom — the most target dollars that keep total employer cost inside the budget.`,
    }))
  }
  const conservative = lo > 500 ? costAtScale(Math.floor(lo * 0.9)) : null
  if (conservative) {
    pushScenario(buildScenario(context, 'budget-cushioned', 'Budget with a margin', 'budget-cushioned', conservative.result, {
      explanation: 'Targets at 90% of the best-fit fill, leaving budget headroom for census drift or a true-up.',
    }))
  }
  return finalizeScenarios(base, scenarios)
}

// Repair: hold the current design; raise NHCE rates to passing, or scale the
// HCE/target dollars down to passing. Never touches the gateway route or the
// group definitions.
function solveRepair(context, base, pushScenario, scenarios) {
  const current = context.options.currentProfitSharingById || {}
  const currentCentsById = Object.fromEntries(
    context.people.map((person) => [person.id, Math.max(0, toCents(current[person.id]))]))
  const asIs = runPipeline(context, 0, { overridesById: currentCentsById })
  if (!asIs.crossTesting.available) {
    return { ...base, status: 'blocked', blockedStatus: makeComplianceStatus({ status: 'unavailable', reasons: [makeReason(asIs.crossTesting.reason || 'unavailable', `Testing is unavailable: ${asIs.crossTesting.reason}.`)] }) }
  }
  if (monotoneTestsPass(asIs)) {
    pushScenario(buildScenario(context, 'repair-none-needed', 'No modeled flags on current design', 'repair-none-needed', asIs, {
      explanation: 'The design as entered does not trigger a modeled screen flag; no modeled repair is suggested. This is not a formal determination.',
    }))
    return finalizeScenarios(base, scenarios)
  }

  // Both repair directions are generated when feasible; the caller's
  // preference orders them in the UI but never hides the alternative.
  {
    // Raise every NHCE's allocation by a uniform add-on rate (monotone).
    const maxAddBp = toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent)
    const withAdd = (bp) => {
      const overrides = { ...currentCentsById }
      for (const person of context.people) {
        if (person.role === ROLE_STAFF && person.includedInAllocation && !person.excludable) {
          overrides[person.id] = (currentCentsById[person.id] || 0)
            + Math.round(person.compCents * bp / 10000)
        }
      }
      return runPipeline(context, 0, { overridesById: overrides })
    }
    if (monotoneTestsPass(withAdd(maxAddBp))) {
      let lo = 0
      let hi = maxAddBp
      while (hi - lo > 1 && context.evaluations < MAX_EVALUATIONS) {
        const mid = Math.floor((lo + hi) / 2)
        if (monotoneTestsPass(withAdd(mid))) hi = mid
        else lo = mid
      }
      pushScenario(buildScenario(context, 'repair-increase-nhce', 'Repair by raising staff', 'repair-increase-nhce', withAdd(hi), {
        explanation: `Adds ${(hi / 100).toFixed(2)}% of pay to every staff allocation — the smallest uniform increase that repairs the failed tests.`,
      }))
    }
  }
  {
    // Scale HCE/target dollars down (monotone decreasing pressure).
    const withScale = (scaleBp) => {
      const overrides = { ...currentCentsById }
      for (const person of context.people) {
        if (person.isHCE) {
          overrides[person.id] = Math.round((currentCentsById[person.id] || 0) * scaleBp / 10000)
        }
      }
      return runPipeline(context, 0, { overridesById: overrides })
    }
    if (monotoneTestsPass(withScale(0))) {
      let lo = 0
      let hi = 10000
      // Find the LARGEST scale that still passes.
      while (hi - lo > 1 && context.evaluations < MAX_EVALUATIONS) {
        const mid = Math.floor((lo + hi) / 2)
        if (monotoneTestsPass(withScale(mid))) lo = mid
        else hi = mid
      }
      pushScenario(buildScenario(context, 'repair-decrease-hce', 'Repair by trimming HCEs', 'repair-decrease-hce', withScale(lo), {
        explanation: `Scales every HCE allocation to ${(lo / 100).toFixed(2)}% of its current amount — the smallest trim that repairs the failed tests.`,
      }))
    }
  }
  if (!scenarios.length) {
    const atMax = runPipeline(context, toBasisPoints(context.options.maxStaffPercent ?? NEW_COMP_MODELING_DEFAULTS.maxStaffProfitSharingPercent))
    return { ...base, status: 'infeasible', diagnostics: diagnoseInfeasible(context, atMax) }
  }
  return finalizeScenarios(base, scenarios)
}
